import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea.jsx';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { Thermometer, AlertTriangle, CheckCircle2, AlertCircle, MapPin, Plus, Loader2, X, Check, Clock, CalendarClock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getDateChips() {
  const chips = [];
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const iso = d.toISOString().split('T')[0];
    const label = i === 0 ? 'Heute' : i === 1 ? 'Gestern'
      : d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
    chips.push({ iso, label, isToday: i === 0 });
  }
  return chips;
}

export const AdminTemperatureInput = () => {
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [measuringPoints, setMeasuringPoints] = useState([]);
  const [todayReadings, setTodayReadings] = useState([]);
  const [todayGrouped, setTodayGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [inlineEntry, setInlineEntry] = useState(null);
  const [inlineSaving, setInlineSaving] = useState(false);

  // Rückwirkende Eingabe
  const dateChips = getDateChips();
  const [selectedDate, setSelectedDate] = useState(dateChips[0].iso);
  const [selectedTime, setSelectedTime] = useState('');
  const [dateReadings, setDateReadings] = useState([]);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchMeasuringPoints();
      fetchDateReadings(selectedDate);
    }
  }, [selectedLocation]);

  // Datum wechselt → Messungen für diesen Tag laden
  const fetchDateReadings = useCallback(async (dateIso) => {
    if (!selectedLocation) return;
    try {
      const res = await axios.get(`${API}/temperature/date-readings`, {
        params: { location_id: selectedLocation, date: dateIso }
      });
      setDateReadings(res.data);
    } catch { /* silent */ }
  }, [selectedLocation]);

  useEffect(() => {
    if (selectedLocation) {
      fetchDateReadings(selectedDate);
      if (selectedDate !== dateChips[0].iso) setSelectedTime('');
    }
  }, [selectedDate, fetchDateReadings]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/admin/locations`);
      setLocations(response.data);
      if (response.data.length > 0) {
        setSelectedLocation(response.data[0].id);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Standorte');
    } finally {
      setLoading(false);
    }
  };

  const fetchMeasuringPoints = async () => {
    try {
      const [pointsRes, todayRes, groupedRes] = await Promise.all([
        axios.get(`${API}/temperature/measuring-points?location_id=${selectedLocation}`),
        axios.get(`${API}/temperature/today?location_id=${selectedLocation}`),
        axios.get(`${API}/temperature/today-grouped?location_id=${selectedLocation}`),
      ]);
      const sorted = pointsRes.data.sort((a, b) => (a.order || 0) - (b.order || 0));
      setMeasuringPoints(sorted);
      setTodayReadings(todayRes.data);
      setTodayGrouped(groupedRes.data || {});
    } catch (error) {
      toast.error('Fehler beim Laden der Messpunkte');
    }
  };

  const isPointMeasuredOnDate = (pointId) => {
    const readings = selectedDate === dateChips[0].iso ? todayReadings : dateReadings;
    return readings.some(r => r.measuring_point_id === pointId);
  };

  const getDateReading = (pointId) => {
    // Für Heute: neueste Messung aus grouped Daten
    if (selectedDate === dateChips[0].iso) {
      return todayGrouped[pointId]?.latest || null;
    }
    return dateReadings.find(r => r.measuring_point_id === pointId) || null;
  };

  const getPointData = (pointId) => {
    if (selectedDate === dateChips[0].iso) {
      return todayGrouped[pointId] || null;
    }
    const readings = dateReadings.filter(r => r.measuring_point_id === pointId);
    if (!readings.length) return null;
    const temps = readings.map(r => r.temperature);
    return { latest: readings[readings.length - 1], all: readings, count: readings.length, daily_average: Math.round(temps.reduce((a,b)=>a+b,0)/temps.length*10)/10 };
  };

  const isReadingInRange = (reading) => {
    if (!reading) return true;
    const point = measuringPoints.find(p => p.id === reading.measuring_point_id);
    if (!point) return true;
    const temp = reading.temperature;
    const minTemp = point.target_temp - point.tolerance;
    const maxTemp = point.target_temp + point.tolerance;
    return temp >= minTemp && temp <= maxTemp;
  };

  // Alarm-Check für Inline-Eingabe
  const getInlineAlarm = (point) => {
    if (!inlineEntry || inlineEntry.pointId !== point.id || inlineEntry.temp === '') return false;
    const temp = parseFloat(inlineEntry.temp);
    if (isNaN(temp)) return false;
    return temp < (point.target_temp - point.tolerance) || temp > (point.target_temp + point.tolerance);
  };

  const handleInlineSave = async (point) => {
    const alarm = getInlineAlarm(point);
    if (alarm && !inlineEntry.comment.trim()) {
      toast.error('Bei Temperaturabweichung ist ein Kommentar PFLICHT!'); return;
    }
    const isToday = selectedDate === dateChips[0].iso;
    setInlineSaving(true);
    try {
      await axios.post(`${API}/temperature`, {
        location_id: selectedLocation,
        measuring_point: point.name,
        measuring_point_id: point.id,
        temperature: parseFloat(inlineEntry.temp),
        comment: inlineEntry.comment || null,
        recorded_by: user.id,
        ...(isToday ? {} : {
          recording_date: selectedDate,
          recording_time: selectedTime || undefined,
        }),
      });
      const suffix = isToday ? '' : ` (Nachtrag ${selectedDate})`;
      toast.success(alarm ? `Temperatur (mit Alarm) gespeichert!${suffix}` : `Temperatur gespeichert!${suffix}`);
      setInlineEntry(null);
      fetchMeasuringPoints();
      fetchDateReadings(selectedDate);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setInlineSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (locations.length === 0) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
              Temperatureingabe
            </h1>
          </div>
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="mx-auto h-12 w-12 text-yellow-500" />
              <p className="mt-4 text-muted-foreground">Kein Standort vorhanden</p>
              <p className="text-sm text-muted-foreground mt-2">
                Bitte erstellen Sie zuerst einen Standort unter Profil → Standort
              </p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
            Temperatureingabe
          </h1>
          <p className="text-muted-foreground mt-2">
            Erfassen Sie die Temperaturen Ihrer Kühlgeräte
          </p>
        </div>

        {/* Location Selector */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Standort auswählen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <select
              value={selectedLocation}
              onChange={(e) => {
                setSelectedLocation(e.target.value);
              }}
              className="w-full h-10 rounded-md border border-input bg-background px-3"
            >
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
          </CardContent>
        </Card>

        {/* Datum-Auswahl (Rückwirkende Eingabe) */}
        {selectedLocation && measuringPoints.length > 0 && (
          <Card>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 mb-3">
                <CalendarClock className="h-4 w-4 text-blue-600 shrink-0" />
                <span className="text-sm font-medium">Messung für welchen Tag?</span>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {dateChips.map(chip => (
                  <button
                    key={chip.iso}
                    onClick={() => setSelectedDate(chip.iso)}
                    data-testid={`date-chip-${chip.iso}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                      selectedDate === chip.iso
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-background border-border text-muted-foreground hover:border-blue-400'
                    }`}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
              {selectedDate !== dateChips[0].iso && (
                <div className="mt-3 flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                  <Clock className="h-4 w-4 text-amber-600 shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-amber-800 dark:text-amber-300 mb-1">
                      Nachtrag für {new Date(selectedDate + 'T12:00:00').toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </p>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-amber-700">Uhrzeit der Messung:</label>
                      <input
                        type="time"
                        value={selectedTime}
                        onChange={e => setSelectedTime(e.target.value)}
                        className="h-7 px-2 rounded-lg border border-amber-300 bg-white dark:bg-gray-900 text-xs"
                        data-testid="backdate-time-input-admin"
                      />
                      <span className="text-xs text-amber-600">(leer = aktuelle Uhrzeit)</span>
                    </div>
                  </div>
                </div>
              )}
              {(() => {
                const readings = selectedDate === dateChips[0].iso ? todayReadings : dateReadings;
                const done = readings.length;
                const total = measuringPoints.length;
                const missing = total - done;
                if (missing === 0 && total > 0) return (
                  <div className="mt-2 flex items-center gap-1.5 text-xs text-green-600">
                    <CheckCircle2 className="h-3.5 w-3.5" />Alle {total} Messpunkte für diesen Tag erfasst
                  </div>
                );
                if (missing > 0) return (
                  <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600">
                    <AlertCircle className="h-3.5 w-3.5" />{missing} von {total} Messpunkt{missing !== 1 ? 'en' : ''} noch nicht erfasst
                  </div>
                );
                return null;
              })()}
            </CardContent>
          </Card>
        )}

        <div>
          {/* Today's Measurements */}
          <Card>
            <CardHeader>
              <CardTitle>
                {selectedDate === dateChips[0].iso ? 'Heutige Messungen' : (
                  <>
                    <span className="text-amber-600">Nachtrag:</span>{' '}
                    {new Date(selectedDate + 'T12:00:00').toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' })}
                  </>
                )}
              </CardTitle>
              <CardDescription>
                {selectedDate === dateChips[0].iso
                  ? 'Übersicht der heute erfassten Temperaturen'
                  : 'Bereits eingetragene Messungen für diesen Tag'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {measuringPoints.map(point => {
                  const reading = getDateReading(point.id);
                  const ptData = getPointData(point.id);
                  const inRange = reading ? isReadingInRange(reading) : null;
                  const isInlineOpen = inlineEntry?.pointId === point.id; // Immer öffnbar
                  const inlineAlarm = isInlineOpen ? getInlineAlarm(point) : false;

                  return (
                    <div
                      key={point.id}
                      className={`rounded-xl border transition-all ${
                        reading
                          ? (inRange
                              ? 'bg-green-50 dark:bg-green-900/20 border-green-200'
                              : 'bg-red-50 dark:bg-red-900/20 border-red-300 border-2')
                          : isInlineOpen
                            ? inlineAlarm
                              ? 'bg-red-50 border-red-300 border-2'
                              : 'bg-amber-50 dark:bg-amber-900/10 border-amber-300 border-2'
                            : 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-200 cursor-pointer hover:border-amber-400 hover:shadow-sm active:scale-[0.99]'
                      }`}
                      onClick={() => {
                        if (!isInlineOpen) {
                          setInlineEntry({ pointId: point.id, temp: '', comment: '', showComment: false });
                        }
                      }}
                      data-testid={`today-point-${point.id}`}
                    >
                      {/* Header-Zeile */}
                      <div className="flex items-center justify-between p-3">
                        <div className="flex items-center gap-2">
                          {reading ? (
                            inRange
                              ? <CheckCircle2 className="h-5 w-5 text-green-600" />
                              : <AlertTriangle className="h-5 w-5 text-red-600" />
                          ) : (
                            <Thermometer className={`h-5 w-5 ${isInlineOpen ? 'text-amber-500' : 'text-yellow-500'}`} />
                          )}
                          <div>
                            <p className="font-medium text-sm">{point.name}</p>
                            <p className="text-xs text-muted-foreground">Soll: {point.target_temp}°C (±{point.tolerance}°C)</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {reading ? (
                            <div className="text-right">
                              <div className="flex items-center gap-1.5">
                                <Badge variant="outline" className={inRange ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                  {reading.temperature}°C {!inRange && '⚠️'}
                                </Badge>
                                {ptData?.count > 1 && (
                                  <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs">
                                    Ø {ptData.daily_average}°C
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                {ptData?.count > 1 ? `${ptData.count}× gemessen` : new Date(reading.created_at).toLocaleTimeString('de-DE', {hour:'2-digit',minute:'2-digit'})}
                              </p>
                            </div>
                          ) : (
                            <Badge variant="outline" className={isInlineOpen ? 'bg-amber-100 text-amber-800' : 'bg-yellow-100 text-yellow-700'}>
                              {isInlineOpen ? 'Eingabe...' : 'Ausstehend'}
                            </Badge>
                          )}
                          {/* Neue Messung Button - bei Abweichung hervorgehoben */}
                          {reading && !isInlineOpen && (
                            <button
                              type="button"
                              onClick={e => { e.stopPropagation(); setInlineEntry({ pointId: point.id, temp: '', comment: '', showComment: false }); }}
                              className={`text-xs px-2 py-1 rounded-lg border transition-colors shrink-0 ${
                                !inRange
                                  ? 'bg-orange-100 text-orange-700 border-orange-400 hover:bg-orange-200 font-semibold'
                                  : 'bg-muted text-muted-foreground border-border hover:border-primary/40'
                              }`}
                            >
                              + Messen
                            </button>
                          )}
                        </div>
                      </div>
                      {/* Inline-Eingabeformular */}
                      {isInlineOpen && (
                        <div
                          className="px-3 pb-3 border-t border-amber-200 pt-3 space-y-2"
                          onClick={e => e.stopPropagation()}
                        >
                          {ptData?.count > 0 && (
                            <p className="text-xs text-muted-foreground">
                              {ptData.count}. Messung heute · Bisheriger Ø: {ptData.daily_average}°C
                            </p>
                          )}
                          {inlineAlarm && (
                            <div className="flex items-center gap-1.5 text-xs text-red-600 font-medium">
                              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                              Außerhalb des Toleranzbereichs! Kommentar ist PFLICHT.
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              step="0.1"
                              placeholder="°C eingeben"
                              value={inlineEntry.temp}
                              onChange={e => setInlineEntry(prev => ({ ...prev, temp: e.target.value }))}
                              className={`flex-1 h-10 text-base ${inlineAlarm ? 'border-red-500 bg-red-50 focus-visible:ring-red-400' : ''}`}
                              autoFocus
                              onKeyDown={e => { if (e.key === 'Enter') handleInlineSave(point); }}
                              data-testid={`inline-temp-${point.id}`}
                            />
                            {!inlineEntry.showComment && !inlineAlarm && (
                              <button
                                type="button"
                                onClick={() => setInlineEntry(prev => ({ ...prev, showComment: true }))}
                                className="h-10 w-10 flex items-center justify-center rounded-md border border-gray-300 text-gray-500 hover:border-amber-400 hover:text-amber-600 shrink-0"
                                title="Kommentar hinzufügen"
                                data-testid={`inline-comment-toggle-${point.id}`}
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                            )}
                            <Button
                              size="sm"
                              className={`h-10 px-3 shrink-0 ${inlineAlarm ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'} text-white`}
                              onClick={() => handleInlineSave(point)}
                              disabled={!inlineEntry.temp || inlineSaving}
                              data-testid={`inline-save-${point.id}`}
                            >
                              {inlineSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                            </Button>
                            <button
                              type="button"
                              onClick={() => setInlineEntry(null)}
                              className="h-10 w-10 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-600 shrink-0"
                              data-testid={`inline-cancel-${point.id}`}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          {(inlineEntry.showComment || inlineAlarm) && (
                            <Textarea
                              placeholder={inlineAlarm ? 'Kommentar PFLICHT – Grund für Abweichung...' : 'Optionaler Kommentar...'}
                              value={inlineEntry.comment}
                              onChange={e => setInlineEntry(prev => ({ ...prev, comment: e.target.value }))}
                              rows={2}
                              className={`text-sm resize-none ${inlineAlarm && !inlineEntry.comment ? 'border-red-500 bg-red-50' : ''}`}
                              data-testid={`inline-comment-${point.id}`}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {measuringPoints.length === 0 && (
                  <div className="text-center py-6 text-muted-foreground">
                    <Thermometer className="mx-auto h-12 w-12 text-muted-foreground/50 mb-2" />
                    <p>Keine Messpunkte für diesen Standort</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};
